import ServiceTemplate from '@/components/ServiceTemplate';

export default function ClearanceSearch() {
  const data = {
    title: 'AI-Enhanced Trademark Clearance Search',
    subtitle: 'Proprietary conflict detection combining machine learning algorithms with forensic-level human analysis—identifying risks traditional searches miss.',
    overviewTitle: 'Why Professional Clearance Search is Non-Negotiable',
    overviewImage: 'https://images.unsplash.com/photo-1763730512449-f1a505f432a9',
    description: [
      'A significant portion of DIY trademark applications face rejection due to undiscovered prior marks—resulting in lost fees, delays, and potential infringement liability. Professional clearance search is not a luxury—it\'s essential risk management that delivers strong ROI through rejection prevention and liability avoidance.',
      'Our proprietary AI-enhanced search protocol analyzes millions of registered marks, pending applications, state registrations across all jurisdictions, common law uses, domain registrations, and social media handles—detecting phonetic similarities, visual confusions, and conceptual overlaps that keyword searches miss entirely.',
      'Board-certified trademark prosecutors provide strategic conflict assessment analyzing likelihood of confusion under multi-factor legal tests, office action probability scoring, and enforcement risk quantification—enabling data-driven filing decisions backed by our industry-leading approval rate.'
    ],
    benefits: [
      { title: 'Quantified Risk Mitigation', description: 'Dramatically reduce rejection probability through AI-enhanced detection of phonetic, visual, and conceptual conflicts across millions of trademark records—preventing lost filing fees and lengthy delays per avoided rejection.' },
      { title: 'Multi-Source Intelligence', description: 'Proprietary search protocol aggregates federal registrations, state filings, common law uses, domain registrations, business directories, and social media handles—detecting conflicts traditional searches miss and preventing costly infringement liability.' },
      { title: 'Strategic Legal Analysis', description: 'Board-certified prosecutors assess likelihood of confusion under DuPont multi-factor test, predict office action probability with 94% accuracy, and quantify enforcement risk—enabling data-driven filing decisions backed by documented precedent analysis.' },
      { title: 'Accelerated Processing', description: '72-hour comprehensive search and analysis delivery versus 7-14 days industry standard—compressing decision timelines by 67% while maintaining forensic-level accuracy through parallel AI and human review protocols.' },
      { title: 'Alternative Strategy Development', description: 'When conflicts exist, receive 3-5 pre-cleared alternative mark recommendations with comparative strength analysis—eliminating iterative search cycles and accelerating brand launch timelines by eliminating trial-and-error approaches.' },
      { title: 'Lifetime Conflict Monitoring', description: 'Post-clearance ongoing monitoring alerts for newly filed confusingly similar marks—enabling proactive opposition filing within critical 30-day windows and preventing competitive encroachment on established brand territories.' }
    ],
    process: [
      { title: 'Research & Analysis', description: 'Comprehensive search and analysis to identify potential conflicts or issues.' },
      { title: 'Application Preparation & Filing', description: 'Expert preparation and filing of complete applications with all required documentation.' },
      { title: 'Monitoring & Approval', description: 'Track application progress and address any office actions until successful approval.' }
    ],
    pricing: [
      {
        name: 'Basic',
        description: 'Essential trademark filing',
        price: '$599',
        period: '+ USPTO fees ($250-$350)',
        features: [
          'Preliminary trademark search',
          'Single class filing',
          'USPTO application preparation',
          'Filing with USPTO',
          'Email support',
          'Status monitoring'
        ]
      },
      {
        name: 'Professional',
        description: 'Complete trademark package',
        price: '$999',
        period: '+ USPTO fees',
        featured: true,
        features: [
          'Everything in Basic',
          'Comprehensive search report',
          'Multi-class discounts available',
          'Office action response (1 included)',
          'Priority processing',
          'Trademark monitoring (6 months)',
          'Phone & email support'
        ]
      },
      {
        name: 'Premium',
        description: 'Full-service trademark protection',
        price: '$1,499',
        period: '+ USPTO fees',
        features: [
          'Everything in Professional',
          'Global trademark search',
          'Unlimited office action responses',
          'International filing coordination',
          'Trademark monitoring (12 months)',
          'Enforcement support',
          'Dedicated attorney access'
        ]
      }
    ],
    faqs: [
      { question: 'How long does a clearance search take?', answer: 'Most clearance searches are completed within 3-5 business days, providing you with a comprehensive report and recommendations.' },
      { question: 'Is a clearance search required?', answer: 'While not legally required, a clearance search is highly recommended to avoid costly conflicts and rejections during the application process.' },
      { question: 'What if conflicts are found?', answer: 'We provide strategic recommendations including potential modifications to your mark or alternative branding options to avoid conflicts.' },
      { question: 'What databases do you search?', answer: 'We search federal and state trademark databases, common law sources, domain name registrations, and business directories for comprehensive coverage.' },
      { question: 'How detailed is the search report?', answer: 'Our reports include identified conflicts, similarity analysis, risk assessment, and strategic recommendations from experienced trademark attorneys.' },
      { question: 'Can I proceed without a search?', answer: 'While possible, proceeding without a search significantly increases rejection risk and potential infringement liability. We strongly recommend conducting a search first.' },
      { question: 'What happens after the search?', answer: 'We review results with you, discuss any concerns, and recommend next steps whether proceeding with your mark or considering alternatives.' },
      { question: 'Do you search international marks?', answer: 'Standard searches focus on U.S. registrations, but we can conduct international searches if you plan to expand globally.' }
    ]
  };

  return <ServiceTemplate {...data} />;
}
